"use client"
import React from "react"
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
  useMotionTemplate,
  useMotionValue,
} from "framer-motion"
import Image from "next/image"
import Link from "next/link"

import Spline from "@splinetool/react-spline"
import { LayeredBadgeCard } from "./layered-badge-card"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

export const HeroParallax = ({
  products,
}: {
  products: {
    id?: string
    title: string
    link?: string
    thumbnail: string
    images?: any[]
    options?: Record<string, string>
  }[]
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)
  const leaveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const firstRow = products.slice(0, 5)
  const secondRow = products.slice(5, 10)
  const thirdRow = products.slice(10, 15)
  const ref = React.useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  })

  const springConfig = { stiffness: 300, damping: 30 }

  const translateX = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 500]),
    springConfig
  )
  const translateXReverse = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, -500]),
    springConfig
  )

  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [0, 1]),
    springConfig
  )

  let mouseX = useMotionValue(0)
  let mouseY = useMotionValue(0)

  function handleMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement>) {
    if (!currentTarget) return
    let { left, top } = currentTarget.getBoundingClientRect()
    mouseX.set(clientX - left)
    mouseY.set(clientY - top)
  }

  return (
    <div
      ref={ref}
      className="h-[320vh] sm:h-[340vh] md:h-[320vh] lg:h-[300vh] py-14 overflow-hidden antialiased relative flex flex-col self-auto mb-24"
    >
      <Header />

      <motion.div
        style={{ opacity }}
        className="relative group"
        onMouseMove={handleMouseMove}
      >
        <motion.div
          className="pointer-events-none bg-dot-thick-white absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100"
          style={{
            WebkitMaskImage: useMotionTemplate`
              radial-gradient(
                200px circle at ${mouseX}px ${mouseY}px,
                black 0%,
                transparent 100%
              )
            `,
          }}
        />

        {/* <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div> */}

        <motion.div className="flex flex-row-reverse gap-12 sm:gap-16 md:gap-20 mb-16 sm:mb-24 px-4">
          {firstRow.map((product, idx) => (
            <LayeredBadgeCard
              product={product}
              translate={translateX}
              key={product.id || product.title}
              index={idx}
              isHovered={hoveredIndex === idx}
              anyCardHovered={hoveredIndex !== null}
              onHover={() => {
                // Clear any existing timeout
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current)
                  leaveTimeoutRef.current = null
                }
                setHoveredIndex(idx)
              }}
              onLeave={() => {
                // Set a timeout to delay the un-dimming
                leaveTimeoutRef.current = setTimeout(() => {
                  setHoveredIndex(null)
                }, 150) // 150ms delay, adjust as needed
              }}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row mb-16 sm:mb-24 gap-12 sm:gap-16 md:gap-20 px-4">
          {secondRow.map((product, idx) => (
            <LayeredBadgeCard
              product={product}
              translate={translateXReverse}
              key={product.id || product.title}
              index={idx + 5}
              isHovered={hoveredIndex === idx + 5}
              anyCardHovered={hoveredIndex !== null}
              onHover={() => {
                // Clear any existing timeout
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current)
                  leaveTimeoutRef.current = null
                }
                setHoveredIndex(idx + 5)
              }}
              onLeave={() => {
                // Set a timeout to delay the un-dimming
                leaveTimeoutRef.current = setTimeout(() => {
                  setHoveredIndex(null)
                }, 150) // 150ms delay, adjust as needed
              }}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row-reverse gap-12 sm:gap-16 md:gap-20 px-4">
          {thirdRow.map((product, idx) => (
            <LayeredBadgeCard
              product={product}
              translate={translateX}
              key={product.id || product.title}
              index={idx + 10}
              isHovered={hoveredIndex === idx + 10}
              anyCardHovered={hoveredIndex !== null}
              onHover={() => {
                // Clear any existing timeout
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current)
                  leaveTimeoutRef.current = null
                }
                setHoveredIndex(idx + 10)
              }}
              onLeave={() => {
                // Set a timeout to delay the un-dimming
                leaveTimeoutRef.current = setTimeout(() => {
                  setHoveredIndex(null)
                }, 150) // 150ms delay, adjust as needed
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  )
}

export const Header = () => {
  return (
    <div className="scale-[0.9] gap-24 md:gap-12 mx-auto max-w-[1240px] flex items-center relative max-w-7xl mx-auto md:py-24 px-6 md:px-12 w-full justify-between flex-col-reverse lg:flex-row">
      <div className="space-y-6 relative flex flex-col justify-center h-[400px]">
        {/* Typography grid markers */}
        <div className="absolute -left-4 top-0 h-full w-[1px] bg-white/10"></div>
        <div className="absolute -left-4 top-0 w-4 h-[1px] bg-white/10"></div>
        <div className="absolute -left-4 bottom-0 w-4 h-[1px] bg-white/10"></div>

        <h1 className="text-6xl max-w-sm md:text-7xl font-bold text-white md:max-w-2xl relative">
          <span className="absolute -left-8 top-0 text-[10px] text-white/30 font-mono">01</span>
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-white/80 drop-shadow-sm">Collect. Connect. Conquer.</span>
        </h1>
        <p className="max-w-2xl text-base md:text-xl mt-4 text-white/80 relative">
          <span className="absolute -left-8 top-0 text-[10px] text-white/30 font-mono">02</span>
          Join an exclusive community of collectors. From limited editions to
          rare variants, your badge is your digital legacy.
        </p>

        <div className="flex pt-6 gap-4 relative">
          <span className="absolute -left-8 top-6 text-[10px] text-white/30 font-mono">03</span>
          <Link
            href="/account"
            className="rounded-sm bg-white/10 backdrop-blur-sm text-white flex items-center space-x-2 font-medium transition-transform duration-300 px-6 py-3 hover:scale-105 border border-white/20 hover:bg-white/20"
          >
            <span>Join Now</span>
          </Link>
          <LocalizedClientLink href="/store" className="bg-gradient-to-r from-white to-white/80 px-6 py-3 rounded-sm font-medium text-black transition-transform duration-300 hover:scale-105">
            Explore Collections
          </LocalizedClientLink>
        </div>
      </div>
      <div className="scale-[0.9] shrink-0 w-[400px] h-[400px] relative overflow-hidden mx-auto md:mx-0 flex items-center justify-center">
        {/* Model typography markers */}
        <div className="absolute top-0 left-0 w-[20px] h-[20px] border-l border-t border-white/20 z-10"></div>
        <div className="absolute top-0 right-0 w-[20px] h-[20px] border-r border-t border-white/20 z-10"></div>
        <div className="absolute bottom-0 left-0 w-[20px] h-[20px] border-l border-b border-white/20 z-10"></div>
        <div className="absolute bottom-0 right-0 w-[20px] h-[20px] border-r border-b border-white/20 z-10"></div>
        <Spline
          className="!h-[400px] !w-[800px] absolute inset-0 left-[-200px]"
          scene="https://prod.spline.design/orZDyhhjhhlVTb4K/scene.splinecode"
        />
      </div>
    </div>
  )
}

export const ProductCard = ({
  product,
  translate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  index,
  isHovered,
  anyCardHovered,
  onHover,
  onLeave,
}: {
  product: {
    title: string
    link?: string
    thumbnail: string
  }
  translate: MotionValue<number>
  index: number
  isHovered: boolean
  anyCardHovered: boolean
  onHover: () => void
  onLeave: () => void
}) => {
  return (
    <motion.div
      style={{
        x: translate,
        transform: `scale(${isHovered ? 1.02 : 1})`,
        filter: `brightness(${isHovered ? 105 : anyCardHovered ? 40 : 100}%)`,
        transition: "filter 300ms ease-out, transform 300ms ease-out",
      }}
      animate={{
        y: isHovered ? -10 : 0,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      key={product.title}
      className="h-96 w-[30rem] relative flex-shrink-0"
    >
      {/* Typography corner markers */}
      <div className={`absolute top-0 left-0 w-[15px] h-[15px] border-l border-t border-white/10 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}></div>
      <div className={`absolute top-0 right-0 w-[15px] h-[15px] border-r border-t border-white/10 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}></div>
      <div className={`absolute bottom-0 left-0 w-[15px] h-[15px] border-l border-b border-white/10 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}></div>
      <div className={`absolute bottom-0 right-0 w-[15px] h-[15px] border-r border-b border-white/10 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}></div>

      <Link href={product.link || "#"} className="block">
        <Image
          src={product.thumbnail}
          height="600"
          width="600"
          className="object-cover object-left-top absolute h-full w-full inset-0"
          alt={product.title}
        />
      </Link>
      <div
        className={`absolute inset-0 w-full h-full opacity-0 transition-opacity duration-300 ${
          isHovered ? "opacity-100" : ""
        }`}
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 25%, transparent 50%)",
          mixBlendMode: "soft-light",
        }}
      />
      <div className={`absolute top-4 left-4 text-[8px] text-white/20 font-mono transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}>
        {(index + 1).toString().padStart(2, '0')}
      </div>
      <h2
        className={`w-full border-b-4 border-white py-1 flex items-center justify-start absolute bottom-4 left-4 text-white z-10 transition-opacity duration-300 ${
          isHovered ? "opacity-100" : "opacity-0"
        }`}
      >
        {product.title}
      </h2>
    </motion.div>
  )
}
